import { useEffect, useState } from 'react'
import { Pencil } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { NumberInput } from '@/components/ui/NumberInput'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { Select } from '@/components/ui/Select'
import { Modal } from '@/components/ui/Modal'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { ParametersService, type Parameter } from '@/services/modules/ParametersService'
import { toast } from '@/components/ui/Toast'
import { useAuthority } from '@/hooks/useAuthority'

/**
 * /settings/parametros — parâmetros globais do sistema.
 *
 * As chaves (`key`) e o tipo são definidos por seed/migration e lidos pelo
 * código; aqui só se ajusta o VALOR (e rótulo/descrição). Não há criar/excluir.
 */
export function SettingsParametersPage() {
  const [reload, setReload] = useState(0)
  const { can } = useAuthority()
  const canEdit = can('settings.parameters.edit')
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(ParametersService, { size: 50, reloadKey: reload })
  const [editing, setEditing] = useState<Parameter | null>(null)

  const exp = useDataTableExport({
    filename: 'parametros',
    service: ParametersService,
    columns: [
      { header: 'Parâmetro', accessor: (p: Parameter) => p.label },
      { header: 'Descrição', accessor: (p: Parameter) => p.description ?? '' },
      { header: 'Grupo',     accessor: (p: Parameter) => p.groupName ?? '' },
      { header: 'Valor',     accessor: (p: Parameter) => p.type === 'MONEY' ? (p.formatted?.value ?? 'R$ 0,00') : (p.value ?? '') },
    ],
  })

  async function handleSave(payload: Partial<Parameter>) {
    if (!editing) return
    try {
      await ParametersService.update(editing.id, payload)
      toast.success({ title: 'Parâmetro atualizado', description: editing.label })
      setEditing(null)
      setReload(v => v + 1)
    } catch (e) {
      toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar.')
    }
  }

  const columns: DataTableColumn<Parameter>[] = [
    {
      key: 'main',
      header: 'Parâmetro',
      alwaysVisible: true,
      render: p => (
        <div>
          <div className="font-medium text-gray-800">{p.label}</div>
          {p.description && <div className="text-[11px] text-gray-500 mt-0.5">{p.description}</div>}
        </div>
      ),
    },
    { key: 'group', header: 'Grupo', align: 'center', className: 'w-40',
      render: p => <span className="text-xs text-gray-600">{p.groupName ?? '—'}</span>,
    },
    { key: 'value', header: 'Valor', align: 'center', className: 'w-40',
      render: p => (
        <span className="font-medium text-gray-800">
          {p.type === 'MONEY' ? (p.formatted?.value ?? 'R$ 0,00') : (p.value ?? '—')}
        </span>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Parâmetros"
        subtitle="Parâmetros globais do sistema"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Parâmetros' }]}
      />
      <DataTable
        tableId="settings.parameters"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={p => p.id}
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        rowActions={(p) => canEdit
          ? [{ key: 'edit', label: 'Editar valor', icon: <Pencil size={14} />, onClick: () => setEditing(p) }]
          : []}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ParameterForm item={editing} onClose={() => setEditing(null)} onSave={handleSave} />
    </>
  )
}

function ParameterForm({ item, onClose, onSave }: {
  item: Parameter | null
  onClose: () => void
  onSave: (payload: Partial<Parameter>) => Promise<void>
}) {
  const open = !!item
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [numValue, setNumValue] = useState<number | null>(null)
  const [strValue, setStrValue] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!item) return
    setLabel(item.label)
    setDescription(item.description ?? '')
    if (item.type === 'MONEY' || item.type === 'INTEGER') {
      setNumValue(item.value !== null && item.value !== '' ? Number(item.value) : null)
    } else {
      setStrValue(item.value ?? '')
    }
  }, [item])

  async function submit() {
    if (!item) return
    setSaving(true)
    const value = (item.type === 'MONEY' || item.type === 'INTEGER')
      ? (numValue ?? 0)
      : strValue
    await onSave({ label: label.trim(), description: description.trim() || null, value: value as unknown as string })
    setSaving(false)
  }

  return (
    <Modal open={open} onClose={onClose}
      title="Editar parâmetro"
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button onClick={submit} loading={saving} disabled={!label.trim()}>Salvar</Button></>}
    >
      {item && (
        <div className="grid grid-cols-1 gap-3">
          <div className="rounded-md bg-gray-50 ring-1 ring-gray-200 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wider text-gray-400">Chave</div>
            <div className="text-xs font-mono text-gray-600">{item.key}</div>
          </div>

          <Input label="Rótulo" required value={label} onChange={e => setLabel(e.target.value)} />

          {item.type === 'MONEY' && (
            <CurrencyInput label="Valor" value={numValue} onChange={setNumValue} />
          )}
          {item.type === 'INTEGER' && (
            <NumberInput label="Valor" value={numValue} onChange={setNumValue} min={0} />
          )}
          {item.type === 'BOOLEAN' && (
            <Select
              label="Valor"
              value={strValue}
              onChange={e => setStrValue(e.target.value)}
              options={[{ value: '1', label: 'Sim' }, { value: '0', label: 'Não' }]}
              searchable={false}
              clearable={false}
            />
          )}
          {item.type === 'TEXT' && (
            <Input label="Valor" value={strValue} onChange={e => setStrValue(e.target.value)} />
          )}

          <Input label="Descrição" value={description} onChange={e => setDescription(e.target.value)} />
        </div>
      )}
    </Modal>
  )
}
